import { TOPICS } from '../content/topics';
import type { DayMark, Progress } from '../store/state';
import { addDays, daysBetween, type IsoDate } from './dates';

// ── Badges ───────────────────────────────────────────────────────────────────

export interface Badge {
  readonly id: string;
  readonly name: string;
  /** Short mark shown in the badge chip. */
  readonly chip: string;
  readonly cond: string;
  readonly need: number;
  readonly get: (progress: Progress) => number;
}

export const BADGES: readonly Badge[] = [
  { id: 'first', name: 'First cut', chip: '01', cond: 'Finish one set', need: 1, get: (p) => p.sets },
  { id: 'd3', name: 'Three in a row', chip: '3d', cond: 'Practise three days running', need: 3, get: (p) => p.longest },
  { id: 'bench', name: 'Bench proof', chip: 'BP', cond: 'Do one bench challenge for real', need: 1, get: (p) => p.bench },
  { id: 'clean3', name: 'Three clean runs', chip: '3x', cond: 'Finish three sets with nothing missed', need: 3, get: (p) => p.cleanRuns.length },
  { id: 'd10', name: 'Ten in a row', chip: '10d', cond: 'Practise ten days running', need: 10, get: (p) => p.longest },
  {
    id: 'allTopics',
    name: 'Every topic touched',
    chip: `${TOPICS.length}`,
    cond: 'Start every topic once',
    need: TOPICS.length,
    get: (p) => TOPICS.filter((t) => (p.best[t.id] ?? 0) > 0).length,
  },
  { id: 'p100', name: 'Hundred worked', chip: '100', cond: 'Work a hundred problems', need: 100, get: (p) => p.solved },
  { id: 'p250', name: 'Two hundred fifty', chip: '250', cond: 'Work 250 problems', need: 250, get: (p) => p.solved },
];

export const BADGE_BY_ID: ReadonlyMap<string, Badge> = new Map(BADGES.map((b) => [b.id, b]));

export function qualifyingBadgeIds(progress: Progress): string[] {
  return BADGES.filter((b) => b.get(progress) >= b.need).map((b) => b.id);
}

/**
 * Fold newly earned badges into progress and report the first new one.
 *
 * Badges are recorded once and never removed. That is the point of them: the
 * streak can break, but nothing she has already done gets taken back. It also
 * fixes a real bug in the artifact prototype, where the bench-challenge badge
 * computed which badges had been gained and then returned `gained.length ? {} : {}`
 * — a no-op, so it could never actually announce one.
 */
export function awardBadges(progress: Progress): { progress: Progress; newBadgeId: string | null } {
  const already = new Set(progress.earnedBadges);
  const gained = qualifyingBadgeIds(progress).filter((id) => !already.has(id));
  if (gained.length === 0) return { progress, newBadgeId: null };

  return {
    progress: { ...progress, earnedBadges: [...progress.earnedBadges, ...gained] },
    newBadgeId: gained[0] ?? null,
  };
}

// ── Streak ───────────────────────────────────────────────────────────────────

/** Banked skip days never exceed this, so a long absence cannot be papered over. */
export const MAX_FREEZES = 3;
/** One skip day earned per this many consecutive days. */
export const FREEZE_EVERY = 7;

/**
 * Record a day of practice and settle the streak.
 *
 * The forgiving part is deliberate and is the single most important mechanic
 * in the app for an ADHD user: a missed day spends a banked skip day rather
 * than wiping the count. Losing a 20-day streak to one bad Tuesday is how
 * people quit, and quitting is the actual failure mode here — not a slightly
 * inflated number.
 */
export function recordPractice(progress: Progress, day: IsoDate): Progress {
  const last = progress.lastPracticeDay;

  // Already counted today. A second set is welcome but does not double-count.
  if (last === day) return { ...progress, days: { ...progress.days, [day]: 'on' } };

  const days: Record<string, DayMark> = { ...progress.days, [day]: 'on' };

  let streak: number;
  let freezes = progress.freezes;

  if (last === null) {
    streak = 1;
  } else {
    const gap = daysBetween(last, day);
    if (gap <= 0) {
      // A clock moved backwards, or an imported backup is from the future.
      // Keep the streak rather than punishing her for someone else's bug.
      return { ...progress, days };
    }
    const missed = gap - 1;
    if (missed === 0) {
      streak = progress.streak + 1;
    } else if (missed <= freezes) {
      // Spend one skip day per missed day and keep the run alive.
      for (let i = 1; i <= missed; i++) days[addDays(last, i)] = 'freeze';
      freezes -= missed;
      streak = progress.streak + missed + 1;
    } else {
      streak = 1;
    }
  }

  // Earn a skip day for each full week of the run, capped.
  if (streak > 0 && streak % FREEZE_EVERY === 0) {
    freezes = Math.min(MAX_FREEZES, freezes + 1);
  }

  return {
    ...progress,
    days: trimDays(days, day),
    streak,
    longest: Math.max(progress.longest, streak),
    freezes,
    lastPracticeDay: day,
  };
}

/** Keep roughly two months of history; the rest is dead weight in storage. */
function trimDays(days: Record<string, DayMark>, today: IsoDate): Record<string, DayMark> {
  const cutoff = addDays(today, -60);
  const out: Record<string, DayMark> = {};
  for (const [date, mark] of Object.entries(days)) {
    if (date >= cutoff) out[date] = mark;
  }
  return out;
}

/** The last seven days, oldest first, for the week strip. */
export function weekMarks(progress: Progress, today: IsoDate): DayMark[] {
  return Array.from({ length: 7 }, (_, i) => progress.days[addDays(today, i - 6)] ?? 'off');
}

/**
 * Settle a streak that has gone stale between sessions.
 *
 * Without this the home screen would keep showing a 6-day streak a fortnight
 * after the last set, because nothing recalculates until she finishes another
 * one. Skip days are spent here too, so opening the app after a couple of
 * missed days shows the run intact rather than a number that is about to
 * silently change.
 */
export function settleStreak(progress: Progress, today: IsoDate): Progress {
  const last = progress.lastPracticeDay;
  if (last === null) return progress;

  const missed = daysBetween(last, today) - 1;
  if (missed <= 0) return progress;

  if (missed <= progress.freezes) {
    const days: Record<string, DayMark> = { ...progress.days };
    for (let i = 1; i <= missed; i++) days[addDays(last, i)] = 'freeze';
    return {
      ...progress,
      days: trimDays(days, today),
      freezes: progress.freezes - missed,
      // The streak is not incremented — she has not practised. It survives.
      lastPracticeDay: addDays(last, missed),
    };
  }

  return { ...progress, days: trimDays(progress.days, today), streak: 0 };
}
